import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatGoal } from "@/lib/marketing/constants";
import { GRADE_TEXT, type Grade } from "@/lib/marketing/grading";
import { cn } from "@/lib/utils";

interface GradedCell {
  value: number | null;
  grade: Grade;
}
interface WebRow {
  month: string;
  hasData: boolean;
  sessions: Record<string, GradedCell>;
  total: GradedCell;
  rates: Record<string, GradedCell>;
}
interface FunnelRow {
  month: string;
  hasData: boolean;
  views: GradedCell;
  submissions: GradedCell;
  conversion: GradedCell;
}
interface WebResponse {
  fiscalYearLabel: string;
  months: string[];
  channels: { key: string; label: string; target: number }[];
  rates: { key: string; label: string; dir: "higher" | "lower"; target: number }[];
  rows: WebRow[];
  funnel: FunnelRow[];
  funnelTargets: { views: number; submissions: number; conversion: number };
}

const fmtInt = (n: number | null) => (n === null ? "—" : Math.round(n).toLocaleString("en-US"));
const fmtPct = (n: number | null) => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);
function fmtMonth(month: string) {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export function WebDashboard() {
  const [data, setData] = useState<WebResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch("/api/marketing-suite/web", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load website data.
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading…</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Traffic by Channel — Monthly ({data.fiscalYearLabel})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Month
                  </th>
                  {data.channels.map((c) => {
                    const goal = formatGoal(c.target, "count", "higher");
                    return (
                      <th
                        key={c.key}
                        className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom"
                      >
                        <div>{c.label}</div>
                        {goal && (
                          <div className="text-[11px] font-normal text-brand-gold">🎯 {goal}</div>
                        )}
                      </th>
                    );
                  })}
                  <th className="text-right py-2 px-2 font-semibold whitespace-nowrap align-bottom">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.month}
                    className={cn("border-b last:border-0", !r.hasData && "opacity-40")}
                  >
                    <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                      {fmtMonth(r.month)}
                    </td>
                    {data.channels.map((c) => {
                      const cell = r.sessions[c.key];
                      return (
                        <td
                          key={c.key}
                          className={cn(
                            "text-right py-2 px-2 tabular-nums",
                            cell && GRADE_TEXT[cell.grade],
                          )}
                        >
                          {r.hasData ? fmtInt(cell?.value ?? null) : "—"}
                        </td>
                      );
                    })}
                    <td
                      className={cn(
                        "text-right py-2 px-2 tabular-nums font-semibold",
                        GRADE_TEXT[r.total.grade],
                      )}
                    >
                      {r.hasData ? fmtInt(r.total.value) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Colored vs. monthly target (weekly benchmark × ~4.3 weeks): 🟢 on/above · 🟡 within 20%
            · 🔴 below
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quote-Form Funnel — Monthly ({data.fiscalYearLabel})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Month
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                    <div>Quote Page Views</div>
                    {formatGoal(data.funnelTargets.views, "count", "higher") && (
                      <div className="text-[11px] font-normal text-brand-gold">
                        🎯 {formatGoal(data.funnelTargets.views, "count", "higher")}
                      </div>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                    <div>Submissions</div>
                    {formatGoal(data.funnelTargets.submissions, "count", "higher") && (
                      <div className="text-[11px] font-normal text-brand-gold">
                        🎯 {formatGoal(data.funnelTargets.submissions, "count", "higher")}
                      </div>
                    )}
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap align-bottom">
                    <div>Conversion Rate</div>
                    {formatGoal(data.funnelTargets.conversion, "ratio", "higher") && (
                      <div className="text-[11px] font-normal text-brand-gold">
                        🎯 {formatGoal(data.funnelTargets.conversion, "ratio", "higher")}
                      </div>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.funnel.map((f) => (
                  <tr
                    key={f.month}
                    className={cn("border-b last:border-0", !f.hasData && "opacity-40")}
                  >
                    <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                      {fmtMonth(f.month)}
                    </td>
                    <td
                      className={cn("text-right py-2 px-3 tabular-nums", GRADE_TEXT[f.views.grade])}
                    >
                      {f.hasData ? fmtInt(f.views.value) : "—"}
                    </td>
                    <td
                      className={cn(
                        "text-right py-2 px-3 tabular-nums",
                        GRADE_TEXT[f.submissions.grade],
                      )}
                    >
                      {f.hasData ? fmtInt(f.submissions.value) : "—"}
                    </td>
                    <td
                      className={cn(
                        "text-right py-2 px-3 tabular-nums font-medium",
                        GRADE_TEXT[f.conversion.grade],
                      )}
                    >
                      {fmtPct(f.conversion.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Quote page views from Google Analytics; submissions are actual quote leads from HubSpot;
            conversion = submissions ÷ views.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Engagement &amp; Site Health — Monthly ({data.fiscalYearLabel})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Month
                  </th>
                  {data.rates.map((r) => {
                    const goal = formatGoal(r.target, "ratio", r.dir);
                    return (
                      <th
                        key={r.key}
                        className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom"
                      >
                        <div>{r.label}</div>
                        {goal && (
                          <div className="text-[11px] font-normal text-brand-gold">🎯 {goal}</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.month}
                    className={cn("border-b last:border-0", !r.hasData && "opacity-40")}
                  >
                    <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                      {fmtMonth(r.month)}
                    </td>
                    {data.rates.map((rate) => {
                      const cell = r.rates[rate.key];
                      return (
                        <td
                          key={rate.key}
                          className={cn(
                            "text-right py-2 px-2 tabular-nums",
                            cell && GRADE_TEXT[cell.grade],
                          )}
                        >
                          {fmtPct(cell?.value ?? null)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            All engagement + bounce rates auto-fill from Google Analytics. Email (SOTU) open/click
            still comes from your email tool — not yet connected.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
